import { createSelector, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { RootState } from "../store";
import { initAuth } from "@/store/features/authSlice";

interface ServiceStatus {
  isInitialized: boolean;
  error: string | null;
}

interface ServicesState {
  auth: ServiceStatus;
  api: ServiceStatus;
}

const initialState: ServicesState = {
  auth: { isInitialized: false, error: null },
  api: { isInitialized: false, error: null },
};

export const initialize = createAsyncThunk(
  "services/initialize",
  async (_, { dispatch }) => {
    await import("@/lib/boot.post");
    await Promise.all([
      // dispatch(initializeDefaultDelay()).unwrap(),
      dispatch(initializeAuth()).unwrap(),
      dispatch(initializeApi()).unwrap(),
    ]);
    return true;
  },
);

export const initializeDefaultDelay = createAsyncThunk(
  "services/initializeDefaultDelay",
  async () => {
    // Simulate delay for better ui experience
    await new Promise(resolve => setTimeout(resolve, 1000));
  },
);

export const initializeAuth = createAsyncThunk(
  "services/initializeAuth",
  async (_, { dispatch }) => {
    await dispatch(initAuth());
  },
);

export const initializeApi = createAsyncThunk("services/initializeApi", async () => {
  await new Promise(resolve => setTimeout(resolve, 1));
  // Add your actual API initialization logic here
  return true;
});

const servicesSlice = createSlice({
  name: "services",
  initialState,
  reducers: {
    resetServices: state => {
      Object.keys(state).forEach(key => {
        state[key as keyof ServicesState] = {
          isInitialized: false,
          error: null,
        };
      });
    },
  },
  extraReducers: builder => {
    // Auth
    builder
      .addCase(initializeAuth.fulfilled, state => {
        state.auth.isInitialized = true;
        state.auth.error = null;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.auth.error = action.error.message || "Failed to initialize auth";
      });

    // API
    builder
      .addCase(initializeApi.fulfilled, state => {
        state.api.isInitialized = true;
        state.api.error = null;
      })
      .addCase(initializeApi.rejected, (state, action) => {
        state.api.error = action.error.message || "Failed to initialize API";
      });
  },
});

// Selectors

export const selectServices = (state: RootState) => state.services;

export const selectAllServicesInitialized = createSelector([selectServices], services =>
  Object.values(services).every(service => service.isInitialized),
);

export const selectServiceErrors = createSelector([selectServices], services =>
  Object.entries(services)
    .filter(([, service]) => service.error !== null)
    .map(([name, service]) => ({
      service: name,
      error: service.error,
    })),
);

export const { resetServices } = servicesSlice.actions;
export default servicesSlice.reducer;

// Optional: Individual service status selectors
export const selectAuthStatus = createSelector(
  [selectServices],
  services => services.auth,
);

export const selectApiStatus = createSelector([selectServices], services => services.api);

// Memoized selector factory for any service
export const makeSelectServiceStatus = (serviceName: keyof ServicesState) =>
  createSelector([selectServices], services => services[serviceName]);

export const selectAllStatuses = createSelector([selectServices], services => services);
